import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Receipt, Plus, FileHeart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const rand = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");
const SCHEMES = ["Discovery", "Bonitas", "Momentum", "GEMS"];

export default function Invoices() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", desc: "", zar: "", medical_aid_portion: "" });
  const canClaim = ["admin", "billing"].includes(user?.role);
  const canPay = ["admin", "billing", "receptionist"].includes(user?.role);

  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: async () => (await api.get("/patients")).data });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/invoices", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); setOpen(false); toast.success("Invoice issued — emitted invoice.issued to n8n"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const claimMut = useMutation({
    mutationFn: async ({ id, scheme }) => (await api.put(`/invoices/${id}/claim`, { scheme })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Claim submitted to medical aid via n8n"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const payMut = useMutation({
    mutationFn: async (id) => (await api.put(`/invoices/${id}/pay`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Marked paid"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Invoices & Claims</h1>
          <p className="mt-1 text-sm text-muted-foreground">{invoices.length} invoices · ZAR</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-btn" className="gap-2 rounded-full"><Plus className="h-4 w-4" /> New invoice</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Patient</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger data-testid="invoice-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Line item</Label><Input data-testid="invoice-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Scale & Polish" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Total (ZAR)</Label><Input type="number" data-testid="invoice-total" value={form.zar} onChange={(e) => setForm({ ...form, zar: e.target.value })} /></div>
                <div><Label>Medical aid portion</Label><Input type="number" data-testid="invoice-ma" value={form.medical_aid_portion} onChange={(e) => setForm({ ...form, medical_aid_portion: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="invoice-submit"
                className="rounded-full"
                disabled={!form.patient_id || !form.zar || createMut.isPending}
                onClick={() => createMut.mutate({
                  patient_id: form.patient_id,
                  line_items: [{ code: "MISC", desc: form.desc || "Consultation", zar: Number(form.zar) }],
                  medical_aid_portion: Number(form.medical_aid_portion || 0),
                })}
              >
                Issue invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden rounded-2xl border-border/60">
        {invoices.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-muted-foreground"><Receipt className="h-8 w-8" /> No invoices yet.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {invoices.map((i) => (
              <div key={i.id} data-testid={`invoice-${i.id}`} className="flex flex-wrap items-center gap-4 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-medium">{i.receipt_no}
                    <Badge variant={i.status === "paid" ? "secondary" : "outline"} className="rounded-full capitalize">{i.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{i.patient_name} · {i.line_items?.map((l) => l.desc).join(", ")}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{rand(i.total)}</div>
                  <div className="text-xs text-muted-foreground">Patient: {rand(i.patient_portion)}</div>
                </div>
                {i.claim ? (
                  <Badge className="gap-1 rounded-full"><FileHeart className="h-3 w-3" /> {i.claim.scheme} · {i.claim.status}</Badge>
                ) : canClaim ? (
                  <Select onValueChange={(scheme) => claimMut.mutate({ id: i.id, scheme })}>
                    <SelectTrigger data-testid={`claim-select-${i.id}`} className="h-9 w-36 rounded-full"><SelectValue placeholder="Submit claim" /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {SCHEMES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : null}
                {i.status !== "paid" && canPay && (
                  <Button size="sm" variant="outline" data-testid={`pay-${i.id}`} onClick={() => payMut.mutate(i.id)} className="gap-1 rounded-full">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
